import { JobDetailsV2Fragment, Location, LocationCreateBase } from 'graphql.generated';
import {  isNil } from 'lodash';

import { FIELD_CONFIG, LocationType } from 'src/app/global.constants';
import { accessTypes, AccessType, ElevatorType, LocationEditorState } from 'src/app/job/job-overview/overview-locations/overview-locations.state';
import { LocationDetailsWizardOption, buildingTypeOptions, buildSizeOptionsForBuildingType, elevatorTypeOptions, buildAccessOptionsForBuildingType } from 'src/app/shared/location-details-wizard/location-details-wizard.selectors';
import { StorageSize, storageSizes, BuildingSize } from 'src/app/shared/location-details-wizard/location-details-wizard.state';
import { ordinalSuffix } from 'src/app/shared/location-details-wizard/location-details-wizard.utils';

export type LocationDetailsEditorKey = 'buildingType' | 'size' | 'access';
export type LocationDetailsLocationType = 'start' | 'end';

export const LOCATION_DETAILS_METADATA_KEYS: Record<LocationDetailsLocationType, Record<LocationDetailsEditorKey, string>> = {
  start: {
    buildingType: 'startBuildingType',
    size: 'startSize',
    access: 'startAccess',
  },
  end: {
    buildingType: 'endBuildingType',
    size: 'endSize',
    access: 'endAccess',
  },
};

export const getLocationDetailsMetadataKey = (
  locationType: LocationDetailsLocationType,
  field: LocationDetailsEditorKey,
) => {
  return LOCATION_DETAILS_METADATA_KEYS[locationType][field];
}

export const toNonEmptyString = (value: unknown): string | undefined => {
  if (isNil(value)) {
    return;
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return;
  }

  return stringValue;
}

export const getLocationDetailsMetadataValue = (
  job: Pick<JobDetailsV2Fragment, 'metadata'>,
  locationType: LocationDetailsLocationType,
  field: LocationDetailsEditorKey,
): string | undefined => {
  const metadata = getJobMetadata(job);

  if (!metadata) {
    return;
  }

  const key = getLocationDetailsMetadataKey(locationType, field);
  return toNonEmptyString(metadata[key]);
}

export const LEGACY_LOCATION_FIELD_NAMES = {
  start: {
    buildingType: 'startLocation.dwellingType',
    bedrooms: 'startLocation.bedrooms',
    area: 'startLocation.sqft',
    floor: 'startLocation.stairs',
    elevators: 'startLocation.elevators',
    elevatorsBookable: 'startLocation.elevatorsBookable',
  },
  end: {
    buildingType: 'endLocation.dwellingType',
    bedrooms: 'endLocation.bedrooms',
    area: 'endLocation.sqft',
    floor: 'endLocation.stairs',
    elevators: 'endLocation.elevators',
    elevatorsBookable: 'endLocation.elevatorsBookable',
  },
} as const;

export type LegacyLocationField = keyof typeof LEGACY_LOCATION_FIELD_NAMES.start;

export const getLegacyLocationFieldValue = (
  job: Pick<JobDetailsV2Fragment, 'fields'>,
  locationType: LocationDetailsLocationType,
  field: LegacyLocationField,
): unknown => {
  const fieldName = LEGACY_LOCATION_FIELD_NAMES[locationType][field];
  const jobField = job.fields?.find((f) => f.name === fieldName);
  return jobField?.values?.[0]?.value;
}

export const getLegacyAccessNotes = (
  job: Pick<JobDetailsV2Fragment, 'fields'>,
  locationType: LocationDetailsLocationType,
): string | undefined => {
  const fieldName = FIELD_CONFIG[locationType].accessNotes.name;
  const accessNotes = job.fields
    ?.find((field) => field.name === fieldName)
    ?.values?.[0]?.value;

  return toNonEmptyString(accessNotes);
}

export const getLegacyBuildingType = (
  job: Pick<JobDetailsV2Fragment, 'fields'>,
  locationType: LocationDetailsLocationType,
): string | undefined => {
  const legacyBuildingType = toNonEmptyString(
    getLegacyLocationFieldValue(job, locationType, 'buildingType'),
  );

  if (!legacyBuildingType) {
    return;
  }

  // Requirement: Normalize legacy building-type labels to canonical BuildingType
  // values (location-details-wizard.state.ts). Intake submits the human-readable
  // label 'Apartment / Condo' (with spaces around the slash); older leads may
  // use the bare 'Apartment' or 'Condo'. Strip whitespace around slashes so
  // any spacing variant collapses to 'Apartment/Condo'.
  const normalized = legacyBuildingType.replace(/\s*\/\s*/g, '/');

  if (normalized === 'Apartment/Condo' || normalized === 'Apartment' || normalized === 'Condo') {
    return 'Apartment/Condo';
  }

  return normalized;
}

export const getLegacySize = (
  job: Pick<JobDetailsV2Fragment, 'fields'>,
  locationType: LocationDetailsLocationType,
): BuildingSize => {
  const buildingType = getLegacyBuildingType(job, locationType);

  if ([ 'Apartment/Condo', 'House', 'Other' ].includes(buildingType)) {

    const bedroomCount = Number(getLegacyLocationFieldValue(job, locationType, 'bedrooms'));

    if (Number.isFinite(bedroomCount)) {
      switch (bedroomCount) {
        case 0:
          return 'studio';
        case 1:
          case 2:
          case 3:
          case 4:
          return `${bedroomCount}bed`
        default:
          return '4bed';
      }
    }
  }

  const areaValueString = getLegacyLocationFieldValue(job, locationType, 'area');

  if (isNil(areaValueString)) return areaValueString;

  const areaValue = Number(areaValueString);

  if (!Number.isFinite(areaValue)) return;

  if ([ 'Storage', 'Storage Unit' ].includes(buildingType)) {

    const dimensions = getStorageSize(areaValue, 20)

    if (dimensions) {
      return dimensions;
    }
  }
}

export function getStorageSize(
  sqft: number,
  threshold: number
): StorageSize | null {
  const values = storageSizes.map(d => ({
    dim: d,
    sqft: dimensionToSqft(d),
  }));

  const min = values[0].sqft;
  const max = values[values.length - 1].sqft;

  if (sqft < min) {
    return min - sqft <= threshold ? values[0].dim : null;
  }

  if (sqft > max) {
    return sqft - max <= threshold ? values[values.length - 1].dim : null;
  }

  let closest = values[0];

  for (const v of values) {
    if (Math.abs(v.sqft - sqft) < Math.abs(closest.sqft - sqft)) {
      closest = v;
    }
  }

  return closest.dim;
}

function dimensionToSqft(dim: StorageSize): number {
  const [w, h] = dim.split('x').map(Number);
  return w * h;
}

const getJobMetadata = (job: Pick<JobDetailsV2Fragment, 'metadata'>): Record<string, unknown> | undefined => {
  return job.metadata as Record<string, unknown> | undefined;
}

export const getBuildingType = (
  job: JobDetailsV2Fragment,
  locationType: LocationDetailsLocationType,
): string | undefined => {
  const buildingTypeMetadataValue = getLocationDetailsMetadataValue(job, locationType, 'buildingType');

  if (buildingTypeMetadataValue) {
    return buildingTypeMetadataValue;
  }

  return getLegacyBuildingType(job, locationType);
}

export const getLocationSize = (
  job: JobDetailsV2Fragment,
  locationType: LocationDetailsLocationType,
): string | undefined => {
  const sizeMetadataValue = getLocationDetailsMetadataValue(job, locationType, 'size');

  if (sizeMetadataValue) {
    return sizeMetadataValue;
  }

  return getLegacySize(job, locationType);
}

export const getLocationAccess = (
  job: JobDetailsV2Fragment,
  locationType: LocationDetailsLocationType,
): AccessType => {
  const accessMetadataValue = getLocationDetailsMetadataValue(job, locationType, 'access');

  if (accessMetadataValue) {
    for (const accessType of accessTypes) {
      if (accessMetadataValue.startsWith(accessType)) {
        return accessType;
      }
    }
  }

  const legacyElevatorsBookable = getLegacyLocationFieldValue(job, locationType, 'elevatorsBookable');

  if (legacyElevatorsBookable === true) {
    return 'elevator';
  }

  const legacyElevators = getLegacyLocationFieldValue(job, locationType, 'elevators');

  if (legacyElevators === 'Freight' || legacyElevators === 'Passanger') {
    return 'elevator';
  }

  const legacyStairs = getLegacyLocationFieldValue(job, locationType, 'floor');

  if (typeof legacyStairs === 'number' && Number.isFinite(legacyStairs)) {
    return legacyStairs === 0 ? 'none' : 'stairs';
  }
}

export const getElevatorType = (
  job: JobDetailsV2Fragment,
  locationType: LocationDetailsLocationType,
): ElevatorType => {

  const accessMetadataValue = getLocationDetailsMetadataValue(job, locationType, 'access');

  if (accessMetadataValue) {
    const [ accessType, rawElevatorType ] = accessMetadataValue.split('_');
    if (accessType === 'elevator' && rawElevatorType && rawElevatorType.includes(rawElevatorType as ElevatorType)) {
      return rawElevatorType as ElevatorType;
    }
  }

  const legacyElevators = getLegacyLocationFieldValue(job, locationType, 'elevators');

  if (legacyElevators === 'Freight') {
    return 'freight';
  }

  const legacyElevatorsBookable = getLegacyLocationFieldValue(job, locationType, 'elevatorsBookable');

  if (legacyElevatorsBookable === true) {
    return 'bookable';
  }

  if (legacyElevatorsBookable === false) {
    return 'not-bookable';
  }
}

export const getFloor = (
  job: JobDetailsV2Fragment,
  locationType: LocationDetailsLocationType,
): number => {

  const accessMetadataValue = getLocationDetailsMetadataValue(job, locationType, 'access');

  if (accessMetadataValue) {

    if (accessMetadataValue.startsWith('none')) {
      return 1;
    }

    const numericMatch = accessMetadataValue.match(/-?\d+/);

    const parsedAccessFloor = numericMatch ? parseInt(numericMatch[0], 10) : undefined;

    if (parsedAccessFloor) {
      return parsedAccessFloor;
    }
  }

  const legacyFloor = getLegacyLocationFieldValue(job, locationType, 'floor');

  if (typeof legacyFloor === 'number' && Number.isFinite(legacyFloor)) {
    return legacyFloor;
  }
}

const withCurrentValue = (options: LocationDetailsWizardOption[], currentValue?: string) => {
  if (!currentValue || (options.map((opt) => opt.value) as string[]).includes(currentValue)) {
    return options;
  }

  return [
    {
      value: currentValue,
      label: currentValue,
    },
    ...options,
  ];
}

export const buildBuildingTypeOptions = (currentValue?: string) => {
  return withCurrentValue(buildingTypeOptions, currentValue);
}

export const buildSizeOptions = ({
  buildingType,
  currentValue,
}: {
  buildingType?: string;
  currentValue?: string;
}) => {
  return withCurrentValue(buildSizeOptionsForBuildingType(buildingType), currentValue);
}

export const buildAccessOptions = ({
  buildingType,
  currentValue,
}: {
  buildingType?: string;
  currentValue?: string;
}) => {
  return withCurrentValue(buildAccessOptionsForBuildingType(buildingType), currentValue);
}

export const buildElevatorTypeOptions = (currentValue?: string) => {
  return withCurrentValue(elevatorTypeOptions, currentValue);
}

export const formatLocationDetails = (editor?: LocationEditorState): string[] => {
  if (!editor) return [];

  const sizeLabel = buildSizeOptions({
    buildingType: editor.buildingType,
    currentValue: editor.size,
  }).find((opt) => opt.value === editor.size)?.label;

  const accessLabel = buildAccessOptions({
    buildingType: editor.buildingType,
    currentValue: editor.access,
  }).find((opt) => opt.value === editor.access)?.label;

  let elevatorTypeLabel: string;

  if (editor.buildingType !== 'Storage Unit') {
    elevatorTypeLabel = buildElevatorTypeOptions(editor.elevatorType)
      .find((opt) => opt.value === editor.elevatorType)?.label;
  }

  let floorLabel: string;

  if (!isNil(editor.floor) && Boolean(editor.access) && !([ 'none', 'outdoors' ].includes(editor.access))) {
    floorLabel = editor.floor === 1 ? 'Ground Floor' : `${editor.floor}${ordinalSuffix(editor.floor)} floor`;
  }

  return [
    editor.buildingType,
    sizeLabel,
    accessLabel,
    elevatorTypeLabel,
    floorLabel,
  ].filter(Boolean);
}

type ElevatorWithType = `elevator_${ElevatorType | 'any'}`

type AccessWithFloor = `${AccessType | ElevatorWithType}_${number}`;

type AccessMetadata = Exclude<AccessType, 'none'> | ElevatorWithType | AccessWithFloor;

export const encodeMetadataAccess = (editor: LocationEditorState): AccessMetadata => {

  if (!editor.access) { return; }

  if (editor.access === 'none') {
    return 'none_1';
  }

  const base: Exclude<AccessType, 'none'> | ElevatorWithType =
    editor.access === 'elevator'
      ? `elevator_${editor.elevatorType ?? 'any'}`
      : editor.access;

  return editor.floor == null
    ? base
    : `${base}_${editor.floor}`;
}

/**
 * Requirement: the "Use end location as billing location" checkbox decides which editor holds
 * the billing address. When it is checked there is no `billing` editor at all — the reducer
 * deletes it — so anything reading `locationEditors.billing` directly silently drops the address
 * the user expects to be saved.
 */
export const resolveBillingLocationEditor = (
  locationEditors: Partial<Record<LocationType, LocationEditorState>>,
  useEndLocationAsBillingLocation: boolean,
): LocationEditorState | undefined => locationEditors?.[useEndLocationAsBillingLocation ? 'end' : 'billing'];

/**
 * Builds the billing address input for a customer mutation.
 *
 * Requirement: the unit number comes from the editor's own `unitNumber` and nothing else.
 * Google autocomplete never populates `selectedLocation.addressLineTwo`, and every reducer that
 * seeds the billing editor copies the stored unit into `unitNumber` -- so falling back to
 * `selectedLocation.addressLineTwo` would only ever resurrect a unit the user had just cleared.
 */
export const resolveBillingAddressInput = (
  locationEditors: Partial<Record<LocationType, LocationEditorState>>,
  useEndLocationAsBillingLocation: boolean,
): LocationCreateBase | undefined => {

  const editor = resolveBillingLocationEditor(locationEditors, useEndLocationAsBillingLocation);

  const selectedLocation = editor?.selectedLocation;

  if (!selectedLocation) { return; }

  const billingAddress: LocationCreateBase = {
    addressLineOne: selectedLocation.addressLineOne,
    addressLineTwo: editor.unitNumber,
    areaCode: selectedLocation.areaCode,
    city: selectedLocation.city,
    country: selectedLocation.country,
    countryJurisdiction: selectedLocation.countryJurisdiction,
  };

  if (selectedLocation.coordinates) {
    billingAddress.coordinates = {
      latitude: selectedLocation.coordinates.latitude,
      longitude: selectedLocation.coordinates.longitude,
    };
  }

  return billingAddress;
}

/**
 * Whether a resolved billing address differs from the one already stored on the customer.
 *
 * Requirement: an edit elsewhere in the customer form must not rewrite the billing address.
 * The resolved address is a mirror of either the end location or the billing editor, both of
 * which hold a value on every save, so without this check every unrelated edit re-creates the
 * Location node and re-points the billing relationship. Compares the two fields the user can
 * actually see and change; a differing unit number alone counts as a change.
 */
export const billingAddressChanged = (
  resolved: LocationCreateBase | undefined,
  stored: Pick<Location, 'addressLineOne' | 'addressLineTwo'> | undefined,
): boolean => {

  if (!resolved) { return false; }

  return (resolved.addressLineOne || '') !== (stored?.addressLineOne || '')
    || (resolved.addressLineTwo || '') !== (stored?.addressLineTwo || '');
}
